import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { OptionsList } from 'gung-common';
import { AuthService, CartRow, CartService, Customer, CustomerProductPrice, PriceService, Product, ProductService, SelectedCustomerService } from 'gung-standard';
import { DeliveryLocationsService, JeevesDeliveryLocation } from 'gung-standard-jeeves';
import { first, forkJoin, map, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-hl-display-edit-checkout-line-modal',
  templateUrl: './hl-display-edit-checkout-line-modal.component.html',
  styleUrl: './hl-display-edit-checkout-line-modal.component.css',
  standalone: false
})
export class HlDisplayEditCheckoutLineModalComponent implements OnInit {
  protected activeModal = inject(NgbActiveModal);
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected priceService = inject(PriceService);
  protected deliveryLocationsService = inject(DeliveryLocationsService);
  protected cartService = inject(CartService);
  protected authService = inject(AuthService);
  protected productService = inject(ProductService);

  public cartRow: CartRow;
  public product: Product;
  protected selectedCustomer: Customer;
  protected productPrice: CustomerProductPrice;
  protected deliveryLocations: JeevesDeliveryLocation[];

  public isLoading: boolean = true;
  public form: FormGroup;
  public inventoryLocationOptions: OptionsList[] = [];
  public pointOfDeliveryOptions: OptionsList[] = [];
  public isCustomerService = false;

  ngOnInit(): void {
    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(
        first(),
        switchMap(customer =>
          forkJoin({
            customer: of(customer),
            productPrice: this.priceService.getCustomerPrices([this.cartRow.productId], customer.id).pipe(
              first(),
              map(prices => prices[0])
            ),
            deliveryLocations: this.deliveryLocationsService.getDeliveryLocationsForCustomer(customer.id).pipe(first()),
            user: this.authService.getCurrentUser().pipe(first()),
            product: this.productService.getProduct(this.cartRow.productId).pipe(first())
          })
        )
      )
      .subscribe(({ customer, productPrice, deliveryLocations, user, product }) => {
        this.selectedCustomer = customer;
        this.productPrice = productPrice;
        this.deliveryLocations = deliveryLocations;
        this.product = product;
        this.isCustomerService = user.roles.filter(role => role.toUpperCase() === 'CUSTOMER_SERVICE').length > 0;
        this.initForm();
      });
  }

  protected initForm(): void {
    this.form = new FormGroup({
      orp: new FormGroup({
        lagstalle: new FormControl(
          { value: this.cartRow?.extra?.orp?.lagstalle || this.selectedCustomer.extra.kus.lagstalle, disabled: false },
          []
        ),
        volymrabatt: new FormControl({ value: this.cartRow?.extra?.orp?.volymrabatt, disabled: false }, []),
        orpradtext: new FormControl({ value: this.cartRow?.extra?.orp?.orpradtext, disabled: false }, []),
        ordlevplats1: new FormControl({ value: this.cartRow?.extra?.orp?.ordlevplats1, disabled: false }, []),
        artnrkund: new FormControl(
          { value: this.selectedCustomer.extra?.kuarMapped?.[this.cartRow.productId]?.artnrkund, disabled: true },
          []
        ),
        priceList: new FormControl({ value: this.productPrice['priceList'], disabled: true }, [])
      }),
      skipBrokenBoxFee: new FormControl({ value: this.cartRow?.extra?._skipBrokenBoxFee, disabled: false }, [])
    });

    this.inventoryLocationOptions = [];

    this.pointOfDeliveryOptions = this.deliveryLocations.map(dl => {
      return {
        id: dl.id.split('_')[1],
        name: `${dl.name} - ${dl.extra.fr?.ftgpostadr1 ?? ''},  ${dl.extra.fr?.ftgpostadr2 ?? ''}, ${dl.extra.fr?.ftgpostnr ?? ''}, ${dl.extra.fr?.ftgpostadr3 ?? ''}`
      };
    });

    this.isLoading = false;
  }

  public onSave(): void {
    const formRawValue = this.form.getRawValue();
    delete formRawValue.orp.artnrkund;
    delete formRawValue.orp.priceList;

    const newExtra = {
      ...structuredClone(this.cartRow.extra)
    };
    newExtra.orp = {
      //...newExtra.orp,
      ...formRawValue.orp
    };
    newExtra._skipBrokenBoxFee = formRawValue.skipBrokenBoxFee;

    this.cartService.setExtra(
      this.cartRow.productId,
      newExtra,
      this.cartRow.targetStockId,
      this.cartRow.productPartialId
    );

    this.close();
  }

  public close(): void {
    this.activeModal.close();
  }
}
